/**
 * Public App Component
 * 
 * Handles public routes (legal pages + marketing pages) that don't require authentication.
 * This is a simple router-less implementation using URL pathname.
 */

import React, { useEffect } from "react";
import { useAuth } from "./hooks/useAuth";
import { LandingPage } from "./pages/LandingPage";
import { PricingPage } from "./pages/PricingPage";
import { SubscribePage } from "./pages/SubscribePage";
import { LoginBridgePage } from "./pages/LoginBridgePage";
import { RegisterPage } from "./pages/RegisterPage";
import { AuthErrorPage } from "./pages/AuthErrorPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { RiskDisclaimerPage } from "./pages/RiskDisclaimerPage";
import { SupportPage } from "./pages/SupportPage";
import { RefundPolicyPage } from "./pages/RefundPolicyPage";
import "./pages/LegalPages.css";

export function PublicApp(): JSX.Element {
  // Get current pathname (without query params)
  const pathname = window.location.pathname;
  
  // Check authentication for landing page redirect
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Check if this is a logout redirect (prevents auto-redirect after logout)
  // Security: Only accept exact "true" value, ignore any other values
  const urlParams = new URLSearchParams(window.location.search);
  const logoutParam = urlParams.get("logout");
  const isLogout = logoutParam === "true";  // Strict equality check for security
  
  // Check if this is a login success redirect (after successful login)
  const loginSuccessParam = urlParams.get("login_success");
  const isLoginSuccess = loginSuccessParam === "true";  // Strict equality check for security
  
  // Redirect authenticated users from landing page (/) to dashboard
  // BUT: Don't redirect if this is a logout redirect (prevents loop)
  useEffect(() => {
    // CRITICAL FIX: If login_success parameter is present, clear logout_flag
    // This ensures that logout_flag doesn't prevent redirect after successful login
    if (isLoginSuccess) {
      // Clean up login_success parameter from URL immediately
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      
      // Clear logout_flag on successful login
      localStorage.removeItem("logout_flag");
      
      // Redirect to dashboard immediately if authenticated
      if (!authLoading && isAuthenticated) {
        window.location.href = "/app";
      }
      return;
    }
    
    // If this is a logout redirect, force isAuthenticated to false and skip redirect
    if (isLogout) {
      // Clean up logout parameter from URL immediately
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      
      // CRITICAL FIX: Set logout flag in localStorage to prevent auto-redirect
      // Use localStorage instead of sessionStorage so it persists across page refreshes
      // This ensures that even if isAuthenticated becomes true (due to cookie delay),
      // we won't redirect the user back to dashboard until they log in again
      localStorage.setItem("logout_flag", "true");
      
      // Don't redirect - user just logged out, show landing page
      return;
    }
    
    // CRITICAL FIX: Check logout flag before redirecting
    // If logout flag is set, don't redirect even if user appears authenticated
    const logoutFlag = localStorage.getItem("logout_flag");
    if (logoutFlag === "true") {
      return;
    }
    
    // Only redirect if user is authenticated and NOT coming from logout
    if (!authLoading && isAuthenticated && (pathname === "/" || pathname === "/index.html")) {
      // Redirect to dashboard (any non-public route will show dashboard)
      // Use /app as a non-public route that will trigger App.tsx to show dashboard
      window.location.href = "/app";
    }
  }, [isAuthenticated, authLoading, pathname, isLogout, isLoginSuccess]);

  // Marketing pages
  if (pathname === "/" || pathname === "/index.html") {
    // Show loading while checking authentication
    if (authLoading) {
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <div>Loading...</div>
        </div>
      );
    }
    // If authenticated, redirect will happen via useEffect above
    // Show landing page for non-authenticated users
    return <LandingPage />;
  }

  if (pathname === "/pricing" || pathname === "/pricing/") {
    return <PricingPage />;
  }

  if (pathname === "/subscribe" || pathname === "/subscribe/") {
    return <SubscribePage />;
  }

  // Auth bridge pages
  if (pathname === "/auth/login" || pathname === "/auth/login/") {
    return <LoginBridgePage />;
  }

  if (pathname === "/auth/register" || pathname === "/auth/register/") {
    return <RegisterPage />;
  }

  if (pathname === "/auth/error" || pathname === "/auth/error/") {
    return <AuthErrorPage />;
  }

  // Legal pages
  if (pathname === "/terms" || pathname === "/terms/") {
    return <TermsPage />;
  }

  if (pathname === "/privacy" || pathname === "/privacy/") {
    return <PrivacyPage />;
  }

  if (pathname === "/risk-disclaimer" || pathname === "/risk-disclaimer/") {
    return <RiskDisclaimerPage />;
  }

  if (pathname === "/support" || pathname === "/support/") {
    return <SupportPage />;
  }

  if (pathname === "/refund-policy" || pathname === "/refund-policy/") {
    return <RefundPolicyPage />;
  }

  // Default: show landing page
  return <LandingPage />;
}

export default PublicApp;

